import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ListChecks, Plus, Trash, CheckCircle, Circle } from "@phosphor-icons/react";
import { Section, Pill, fmtDT, err } from "./hrUtils";
import { TONE_CLASSES } from "../assets/assetUtils";

/** Onboarding / offboarding checklists on a record. HR ticks anything; the employee ticks only their own steps. */
export function ChecklistsCard({ emp, hr, onEmployeeChange }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => API.get(`/hr/employees/${emp.id}/checklists`).then((r) => setData(r.data)).catch(() => setData({ checklists: [], owners: {} })), [emp.id]);
  useEffect(() => { load(); }, [load]);
  const onChange = (completed) => { load(); if (completed) onEmployeeChange?.(); };

  const start = async (kind) => {
    try { const r = await API.post(`/hr/employees/${emp.id}/checklists`, { kind }); toast.success(r.data.message); load(); } catch (e) { err(e, "Couldn't start checklist"); }
  };
  const lists = data?.checklists || [];
  const hasOpen = (k) => lists.some((c) => c.kind === k && !c.completed_at);

  return (
    <Section title="Onboarding & offboarding" icon={ListChecks} testid="hr-card-checklists" action={hr && (
      <div className="flex gap-1">
        {!hasOpen("onboarding") && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => start("onboarding")} data-testid="hr-checklist-start-onboarding">Start onboarding</Button>}
        {!hasOpen("offboarding") && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => start("offboarding")} data-testid="hr-checklist-start-offboarding">Start offboarding</Button>}
      </div>
    )}>
      {!data ? <p className="text-xs text-muted-foreground">Loading…</p> : lists.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="hr-checklists-empty">{hr ? "No checklists yet — start onboarding to track the first-week steps." : "Nothing for you to tick off right now."}</p>
      ) : lists.map((c) => <Checklist key={c.id} c={c} hr={hr} owners={data.owners} onChange={onChange} />)}
    </Section>
  );
}

function Checklist({ c, hr, owners, onChange }) {
  const [text, setText] = useState("");
  const [owner, setOwner] = useState("hr");
  const tick = async (it) => {
    try { const r = await API.put(`/hr/checklists/${c.id}/items/${it.id}`, { done: !it.done }); if (r.data.completed_at && !it.done) toast.success(r.data.message); onChange(!!r.data.completed_at); } catch (e) { err(e, "Couldn't update step"); }
  };
  const add = async () => {
    if (!text.trim()) return;
    try { await API.post(`/hr/checklists/${c.id}/items`, { text, owner }); setText(""); onChange(); } catch (e) { err(e, "Couldn't add step"); }
  };
  const remove = async () => {
    if (!window.confirm("Remove this checklist?")) return;
    try { await API.delete(`/hr/checklists/${c.id}`); onChange(); } catch (e) { err(e, "Couldn't remove"); }
  };
  return (
    <div className="rounded-lg border border-border p-3 space-y-2" data-testid={`hr-checklist-${c.kind}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold capitalize">{c.kind}</p>
          <Pill tone={c.completed_at ? TONE_CLASSES.ok : TONE_CLASSES.soon} testid={`hr-checklist-${c.kind}-progress`}>{c.completed_at ? "Complete" : `${c.done_count}/${c.total} done`}</Pill>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          <span>Started {fmtDT(c.started_at)}</span>
          {hr && <button onClick={remove} className="text-red-500 hover:text-red-700" aria-label="Remove checklist" data-testid={`hr-checklist-${c.kind}-delete`}><Trash size={13} /></button>}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${c.progress}%` }} /></div>
      <ul className="space-y-1">
        {c.items.map((it) => {
          const mine = hr || it.owner === "employee";
          return (
            <li key={it.id} className="flex items-start gap-2 text-sm">
              <button disabled={!mine} onClick={() => tick(it)} className={`mt-0.5 ${mine ? "text-primary" : "text-muted-foreground/50 cursor-not-allowed"}`} aria-label={it.done ? "Reopen step" : "Tick step"} data-testid={`hr-step-${it.id}`}>
                {it.done ? <CheckCircle size={18} weight="fill" /> : <Circle size={18} />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={it.done ? "line-through text-muted-foreground" : ""}>{it.text}</p>
                <p className="text-[11px] text-muted-foreground">{owners[it.owner] || it.owner}{it.done && it.done_by ? ` · ticked by ${it.done_by} ${fmtDT(it.done_at)}` : ""}</p>
              </div>
            </li>
          );
        })}
      </ul>
      {hr && !c.completed_at && (
        <div className="flex gap-2 pt-1">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a step…" className="h-8 text-sm" data-testid={`hr-checklist-${c.kind}-new-text`} />
          <select value={owner} onChange={(e) => setOwner(e.target.value)} className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid={`hr-checklist-${c.kind}-new-owner`}>
            {Object.entries(owners).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-8" onClick={add} data-testid={`hr-checklist-${c.kind}-new-add`}><Plus size={14} /></Button>
        </div>
      )}
    </div>
  );
}
